import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

function Metric({ label, value }) {
  return (
    <Card className="p-4" data-testid={`ra-perf-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="text-2xl font-display font-bold">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </Card>
  );
}

export default function RAPerformance({ team = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const url = team ? "/ra/team-performance" : "/ra/performance";
    api.get(url).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, [team]);

  if (!data) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-48" /></div>;

  if (team) {
    return (
      <div>
        <PageHeader title="R.A. Team Performance" subtitle="Quality-weighted performance across your Research Analysts" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Metric label="Total Leads" value={data.totals.total_leads} />
          <Metric label="Outreach" value={data.totals.outreach} />
          <Metric label="Replies" value={data.totals.replies} />
          <Metric label="Positive Leads" value={data.totals.positive_leads} />
        </div>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ra-team-table">
            <thead><tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="p-3">Analyst</th><th className="p-3">Leads</th><th className="p-3">Outreach</th><th className="p-3">Replies</th>
              <th className="p-3">Positive</th><th className="p-3">Reply %</th><th className="p-3">Positive %</th><th className="p-3">Data Quality</th><th className="p-3">Dup Overrides</th>
            </tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.ra_user_id} className="border-b border-border/60" data-testid={`ra-team-row-${r.ra_user_id}`}>
                  <td className="p-3 font-medium">{r.ra_name}</td>
                  <td className="p-3">{r.total_leads}</td><td className="p-3">{r.outreach}</td><td className="p-3">{r.replies}</td>
                  <td className="p-3">{r.positive_leads}</td><td className="p-3">{r.reply_conversion}%</td><td className="p-3">{r.positive_conversion}%</td>
                  <td className="p-3">{r.data_quality}%</td><td className="p-3">{r.duplicate_overrides}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    );
  }

  const p = data.performance;
  return (
    <div>
      <PageHeader title="My Performance" subtitle="Accuracy and conversion, not just volume" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Total Leads" value={p.total_leads} />
        <Metric label="Outreach Sent" value={p.outreach} />
        <Metric label="Replies" value={p.replies} />
        <Metric label="Positive Leads" value={p.positive_leads} />
        <Metric label="Valid Emails" value={p.valid_emails} />
        <Metric label="Reply Conversion" value={`${p.reply_conversion}%`} />
        <Metric label="Positive Conversion" value={`${p.positive_conversion}%`} />
        <Metric label="Data Quality" value={`${p.data_quality}%`} />
      </div>
    </div>
  );
}
